import React from 'react';
import { DataContext } from '../DataProvider';
import styled from 'styled-components';
import * as Dialog from '@radix-ui/react-dialog';

function Controls() {
  const {
    navigateBack,
    navigateForward,
    currentNode,
    currentMode,
  } = React.useContext(DataContext);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [lastForwardPress, setLastForwardPress] = React.useState(0);

  const handleForward = () => {
    if (!currentNode || currentNode.children.length === 0) return;

    if (currentNode.children.length > 1) {
      setIsDialogOpen(true);
    } else {
      navigateForward(0);
    }
  };

  const handleForwardKey = () => {
    if (!currentNode || currentNode.children.length === 0) return;

    const now = Date.now();
    const timeSinceLastPress = now - lastForwardPress;

    if (currentNode.children.length > 1) {
      if (timeSinceLastPress < 300) {
        navigateForward(0);
        setLastForwardPress(0);
        setIsDialogOpen(false);
      } else {
        setIsDialogOpen(true);
        setLastForwardPress(now);
      }
    } else {
      navigateForward(0);
    }
  };

  const chooseVariation = (index) => {
    navigateForward(index);
    setIsDialogOpen(false);
  };

  const canGoBack = currentNode && currentNode.parent;
  const canGoForward = currentNode && currentNode.children.length > 0;

  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (currentMode !== 'builder') return;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          if (canGoBack) {
            navigateBack();
          }
          break;
        case 'ArrowRight':
          event.preventDefault();
          if (canGoForward) {
            handleForwardKey();
          }
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentMode, canGoBack, canGoForward, navigateBack, handleForwardKey]);

  return (
    <ControlsWrapper>
      {currentMode === 'builder' ? (
        <>
          <ControlButton
            onClick={navigateBack}
            disabled={!canGoBack}
            title='Previous Move (←)'
          >
            {'<'}
          </ControlButton>
          <ControlButton
            onClick={handleForward}
            disabled={!canGoForward}
            title='Next Move (→)'
          >
            {'>'}
          </ControlButton>
        </>
      ) : null}

      {currentNode && (
        <VariationDialog
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          variations={currentNode.children}
          onChoose={chooseVariation}
        />
      )}
    </ControlsWrapper>
  );
}

const VariationDialog = ({ isOpen, onClose, variations, onChoose }) => {
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  React.useEffect(() => {
    if (isOpen) {
      setSelectedIndex(0);
    }
  }, [isOpen]);

  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isOpen) return;

      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex((prev) =>
            prev > 0 ? prev - 1 : variations.length - 1
          );
          break;
        case 'ArrowDown':
          event.preventDefault();
          setSelectedIndex((prev) =>
            prev < variations.length - 1 ? prev + 1 : 0
          );
          break;
        case 'Enter':
          event.preventDefault();
          onChoose(selectedIndex);
          break;
        case 'Escape':
          event.preventDefault();
          onClose();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, selectedIndex, variations.length, onChoose, onClose]);

  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <DialogOverlay />
        <DialogContent>
          <DialogTitle>Choose Continuation</DialogTitle>
          <DialogDescription>
            Multiple moves found. Select the line to follow.
          </DialogDescription>
          <VariationList>
            {variations.map((node, index) => (
              <li key={node.fen}>
                <VariationButton
                  onClick={() => onChoose(index)}
                  $selected={index === selectedIndex}
                >
                  {node.move.san}
                </VariationButton>
              </li>
            ))}
          </VariationList>
          <Dialog.Close asChild>
            <CloseButton aria-label='Close'>X</CloseButton>
          </Dialog.Close>
        </DialogContent>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default Controls;

const ControlsWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 18px;
  flex-wrap: wrap;
`;

const ControlButton = styled.button`
  padding: 12px 18px;
  min-width: 60px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  color: var(--accent-strong);
  background: var(--surface-strong);
  border: 1px solid var(--border);
  border-radius: 999px;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    background 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--accent-ghost);
    transform: translateY(-1px);
    box-shadow: 0 12px 24px rgba(86, 62, 43, 0.12);
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

const overlayShow = `
  @keyframes overlayShow {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const contentShow = `
  @keyframes contentShow {
    from { opacity: 0; transform: translate(-50%, -48%) scale(0.96); }
    to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  }
`;

const DialogOverlay = styled(Dialog.Overlay)`
  background-color: rgba(47, 38, 31, 0.32);
  position: fixed;
  inset: 0;
  animation: overlayShow 150ms cubic-bezier(0.16, 1, 0.3, 1);
  ${overlayShow}
`;

const DialogContent = styled(Dialog.Content)`
  background: var(--surface-strong);
  border-radius: 24px;
  border: 1px solid var(--border);
  box-shadow:
    0 20px 38px -18px rgba(0, 0, 0, 0.22),
    0 16px 24px -20px rgba(0, 0, 0, 0.2);
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90vw;
  max-width: 380px;
  padding: 28px;
  animation: contentShow 150ms cubic-bezier(0.16, 1, 0.3, 1);
  ${contentShow}
`;

const DialogTitle = styled(Dialog.Title)`
  margin: 0;
  font-weight: 600;
  font-size: 1.3rem;
  color: var(--text);
`;

const DialogDescription = styled(Dialog.Description)`
  margin: 10px 0 20px;
  font-size: 0.95rem;
  line-height: 1.5;
  color: var(--text-soft);
`;

const VariationList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const VariationButton = styled.button`
  width: 100%;
  padding: 12px 14px;
  text-align: left;
  background-color: ${(props) =>
    props.$selected ? 'var(--accent-soft)' : 'rgba(255, 249, 241, 0.92)'};
  color: ${(props) => (props.$selected ? 'var(--accent-strong)' : 'var(--text)')};
  border: 1px solid
    ${(props) =>
      props.$selected ? 'rgba(159, 109, 76, 0.3)' : 'var(--border)'};
  border-radius: 14px;
  cursor: pointer;

  &:hover {
    background-color: ${(props) =>
      props.$selected ? '#ecdccf' : 'rgba(239, 228, 213, 0.82)'};
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1px solid var(--border);
  color: var(--text-soft);
  background: rgba(255, 249, 241, 0.92);
`;
